// In-memory outbox of a user chart's files waiting to be uploaded to the server,
// keyed by chartId: the processed tiles archive (the {z}/{x}/{y}.png ZIP) and the
// imported source file (the original PDF/TIFF/MBTiles).
//
// A chart converted on the web holds its tiles in the in-memory tile store
// (userChartTileStore) and its source ZIP + original here until the next sync
// uploads them. UserChartSyncService reads from this outbox when pushing pending
// charts: it POSTs the ZIP to `/userChart/{id}/processed` and the original to
// `/userChart/{id}/original`, clearing each on success. Once uploaded, the server is
// the source of truth: any browser downloads the tiles via `downloadProcessed`, and
// a chart whose tiles are missing can be re-hydrated by re-converting `downloadOriginal`.
//
// The planning-app is online-first and persists nothing locally (unlike the mobile
// pilotapp): chart metadata and tiles are re-fetched from the server on every boot and
// kept only in memory. This outbox is likewise memory-only — it just bridges the gap
// between a local conversion and the background upload within the same session. If the
// page reloads before the upload finishes, the entry is gone (the chart is recoverable
// by re-uploading its file); the user is warned about this after saving.

interface OutboxEntry {
  archive?: Uint8Array
  original?: Blob
  originalName?: string
}

const outbox = new Map<string, OutboxEntry>()

function toUint8Array(archive: ArrayBuffer | Uint8Array): Uint8Array {
  return archive instanceof Uint8Array ? archive : new Uint8Array(archive)
}

// Merges a patch into a chart's entry, dropping the entry when it holds no file.
function patchEntry(chartId: string, patch: OutboxEntry): void {
  const next: OutboxEntry = { ...outbox.get(chartId), ...patch }
  if (!next.archive && !next.original) {
    outbox.delete(chartId)
    return
  }
  outbox.set(chartId, next)
}

// --- Processed tiles archive ---------------------------------------------------

export function setPendingProcessedUpload(chartId: string, archive: ArrayBuffer | Uint8Array): void {
  patchEntry(chartId, { archive: toUint8Array(archive) })
}

export function getPendingProcessedUpload(chartId: string): Uint8Array | null {
  return outbox.get(chartId)?.archive ?? null
}

export function clearPendingProcessedUpload(chartId: string): void {
  patchEntry(chartId, { archive: undefined })
}

// --- Original source file ------------------------------------------------------

export function setPendingOriginalUpload(chartId: string, original: Blob, originalName: string): void {
  patchEntry(chartId, { original, originalName })
}

export function getPendingOriginalUpload(chartId: string): { blob: Blob; name: string } | null {
  const entry = outbox.get(chartId)
  if (!entry?.original) return null
  return { blob: entry.original, name: entry.originalName ?? chartId }
}

export function clearPendingOriginalUpload(chartId: string): void {
  patchEntry(chartId, { original: undefined, originalName: undefined })
}

// --- Bulk / cleanup ------------------------------------------------------------

// Every chart that still has a pending processed archive.
export function getAllPendingProcessedUploads(): { id: string; archive: Uint8Array }[] {
  const result: { id: string; archive: Uint8Array }[] = []
  for (const [id, entry] of outbox) {
    if (entry.archive) result.push({ id, archive: entry.archive })
  }
  return result
}

// Every chart that still has a pending original upload.
export function getAllPendingOriginalUploads(): { id: string; blob: Blob; name: string }[] {
  const result: { id: string; blob: Blob; name: string }[] = []
  for (const [id, entry] of outbox) {
    if (entry.original) result.push({ id, blob: entry.original, name: entry.originalName ?? id })
  }
  return result
}

// Drops the whole entry (both files) for a chart — used on hard-delete of a chart.
export function clearPendingUpload(chartId: string): void {
  outbox.delete(chartId)
}

export function clearAllPendingProcessedUploads(): void {
  outbox.clear()
}
